
import Raphael from 'raphael';
import { ElemHomeView, ElemCard } from './ElemHomeView';
import { ElemBigView, BigCard } from './ElemBigView';
import { ElemPlayView } from './ElemPlayView';

export default class ElemPlayContainer {

    constructor( Dyn, dataSet, plotObj, onStateChange ) {

        // references to other objects
        this.Dyn = Dyn;
        this.Dyn.elemPlayContainer = this;
        this.dataSet = dataSet; // reference to Dyn.DataSet
        this.plotObj = plotObj;
        this.onStateChange = onStateChange;
        this.subscriptions = [];

        // our data
        this.options = {}; // isIconOnly, etc
        this.state = {}; // where are the cards? home? in big cards? in play area?
        this.sizes = {};
        this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // position of paper
        this.paper = null;	// Rafael canvas
        this.defaultTipAttributes = this.dataSet.getTooltipAttributes(); // from metadata
        if( ! this.defaultTipAttributes.length ) {
            this.defaultTipAttributes = [ // or default to first attribute other than "Case"
                this.dataSet.attribs.find((attr)=>!attr.isCaseAttribute)
            ].filter( attr => !!attr );
        }

        // subviews (overlapping areas drawing on .paper within .bounds
        this.home = new ElemHomeView( Dyn, this ); // manages small cards and area for small card outlines
        this.big = new ElemBigView( Dyn, this ); // manages big cards and area for big cards
        this.play = new ElemPlayView( Dyn, this ); // manages play area and orderby/groupby plots

        this.setState( this.big.getBigCardState());
    }

    destroy() {
        // Remove subscribes
        this.Dyn.mediatorLocalRemoveAll(this.subscriptions);

        // destroy sub-objects
        this.home.destroy(); this.home = null;
        this.big.destroy(); this.big = null;
        this.play.destroy(); this.home = null;

        // destroy SVG layer in view
        this.Dyn.plotUtil.destroyPaperAndSVGObjects( this.paper );
        this.paper = null;

        // remove references
        let Dyn = this.Dyn;
        this.Dyn = null;
        this.dataSet = null;
        this.plotObj = null;

        // notify dependent objects
        Dyn.mediator.publish("elemPlayContainer:destroy");
    }

    // Initialize the plot using dataSet
    initVisuals( sizes, plotState=null ) {
        let Dyn = this.Dyn;
        this.sizes = Object.assign({},sizes);
        this.sizes.containerWidth = this._minZero( this.plotObj.width());
        this.sizes.containerHeight = this._minZero( this.plotObj.height());

        // create the plotting area
        this.paper = Raphael( this.plotObj[0], this.sizes.containerWidth, this.sizes.containerHeight );
        this.home.initVisuals();
        this.big.initVisuals();
        this.play.initVisuals();
        this._resizeViews();

        // initialize the tooltip utility (one per plot, which gets positioned over any one element on hover-over)
		Dyn.toolTip = new Dyn.ToolTip( this.paper, this.plotObj );

        // handle plot resize
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:containerResized", (args) => this.handlePlotResize( args ) );
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newTheme", (args) => this.handleColorThemeChange( args.themeName) );
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setAttributeMetadata", (args) => this.handleUpdatedAttribute( args ));

        this.restoreFromPlotState( plotState ); // restore saved state if any
    }

    updateVisuals() {
        this.home.updateVisuals();
        this.big.updateVisuals();
        this.play.updateVisuals();
    }

    handleColorThemeChange( themeName ) {
        this.Dyn.plotUtil.setThemeColors( themeName );
        this.updateVisuals();
    }

    handlePlotResize( args ) {
        // reset the SVG canvas/paper
        this.sizes.containerWidth = this._minZero( this.plotObj.width() );
        this.sizes.containerHeight = this._minZero( this.plotObj.height() );
        if( args.cardAreaWidth > 0 ) {
            this.sizes.cardAreaWidth = args.cardAreaWidth;
        }
        this._resizeViews();
        this.updateVisuals();
    }

    handleUpdatedAttribute( args ) {
        this.updateVisuals(); // rebuild plot and home with new attribute
    }

    _resizeViews() {
        let Dyn = this.Dyn;
        this.paper.setSize( this.sizes.containerWidth, this.sizes.containerHeight );
        this.home.resize( new Dyn.mathUtil.Rect(
            0, 0,
            this.sizes.cardAreaWidth, this.sizes.containerHeight ));
        const playNBigLeft = this.sizes.cardAreaWidth+this.sizes.cardPlayBorder;
        this.play.resize( new Dyn.mathUtil.Rect(
            playNBigLeft, 0,
            this._minZero( this.sizes.containerWidth - playNBigLeft ),
            this._minZero( this.sizes.containerHeight - this.sizes.bottomToolbarHeight )));
        this.big.resize( new Dyn.mathUtil.Rect(
            this.sizes.cardAreaWidth+this.sizes.cardPlayBorder, 0,
            this._minZero( this.sizes.containerWidth - playNBigLeft ),
            this._minZero( this.sizes.containerHeight - this.sizes.bottomToolbarHeight )));
        this.layerElements();
    }
    _minZero( n ) { return Math.max(0,n); }

    /** state of the Play area. Coordinate the state options with:
     * ElemHomeView.getHomeState(),
     * ElemBigView.getBigCardState(),
     * this.groupBy(), this.orderBy()
     * @param state
     */
    setState( state ) {
        const lastPlot = this.state?.plot;

        switch( state?.plot ) {
            case "home":
            case "leftHome":
            case "bigCards":
                this.options = {};
                this.state = state; // set new state; clear old state
                this.play.clearTitles();
                break;
            case "orderBy":
            case "groupBy":
                // set new state, but preserve options like isSpreadOut, isIconOnly, isShowingCount
                this.state = state;
                break;
            default:
                this.state = { plot:"home" };
                this.Dyn.assert( "undefined play state: "+state?.plot.toString() );
        }
        // When plot is changed, refresh react parent component
        if(lastPlot != this.state.plot) {
            this.onStateChange({state: this.state});
        }
        //console.log( "setState", state );
    }

    /* API for react control states */
    get canShowPlotControls() {
        return [ "bigCards", "orderBy", "groupBy" ].includes( this.state?.plot );
    }

    /** show the action buttons used by OrderBy or GroupBy plots? */
    get canShowActionButtons() {
        return ["groupBy", "orderBy"].includes( this.state?.plot );
    }

    /** disable (grey out) the Reset button? */
    get canDisableResetButton() {
        return ["home"].includes( this.state?.plot );
    }

    /** disable (grey out) the Plot View button? */
    get canDisablePlotViewButton() {
        let isOrderOrGroupBy = ["groupBy", "orderBy"].includes( this.state?.plot ),
            hasNoCards = (0 === this.dataSet.getNumCasesAfterFilter());
        // switch to plot view ONLY if state is OrderBy or GroupBy,
        // EXCEPT if user has filtered out all cases, then Reset, because at that point there is no card to drag,
        // 	and no way to make a plot. The workaround is to enable the Plot View Button so they can change filtering again.
        return isOrderOrGroupBy || hasNoCards;
    }

    /** save the play data for later .restore() */
    save() {
        let data = {
            plot: this.state.plot,
            options: Object.assign({}, this.options),
            cards: this.home.saveCards()
        }
        if( this.state.attribute ) {
            data.attributeID = this.state.attribute.id;
        }
        if( this.state.cases ) {
            this.Dyn.assert( this.state.plot==='bigCards');
            data.bigCards = { cases: this.state.cases };
        }
        return data;
    }

    /** restore the play container from the results of a previous .save() */
    restore( data ) {
        let attribute = this.dataSet.getAttributeByID( data?.attributeID ); // null if data missing

        // get options first
        this.options.isSpreadOut = Boolean( data?.options?.isSpreadOut );
        this.options.isIconOnly = Boolean( data?.options?.isIconOnly );
        this.options.isShowingCount = Boolean( data?.options?.isShowingCount );

        // restore or reset plot
        switch( data?.plot ) {
            case "groupBy":
            case "orderBy":
                this.play.restorePlot( data?.plot, attribute );
                this.home.restoreCards( data?.cards );
                break;
            case "bigCards":
                this.play.resetPlay();
                this.big.restoreBigCards( data?.bigCards );
                this.home.restoreCards( data?.cards );
                break;
            case "home":
            default:
                this.resetPlay();
                this.home.restoreCards( data?.cards );
        }
    }

    // save the plot state, for when PlotView.save() is not possible
    savePlotState() {
        return {
            playState: this.save(),
            dataSet: this.dataSet.save() // save data modifications, like PlotState.save()
        };
    }

    // restore the plot state to a play view, for when PlotView.restore() is not possible
    restoreFromPlotState( plotState ) {
        // first restore dataset modifications, just like PlotView.restore()
        if( plotState?.dataSet ) {
            this.dataSet.restore( plotState.dataSet, true /* deferPlotUpdate */ );
            this.home.updateVisuals(); // make filter changes from dataset if needed
            this.play.updateFilterDisplay();
        }
        // then restore play state
        if( plotState?.playState ) {
            this.restore( plotState.playState ); // restore from play state
        } else if ( plotState?.plotShaper ) {
            this.restore( this.switchIntoPlayView( plotState )); // or from plot state
        }
    }

    /** return true if Play View state is in any type of plot, or optionally just the one plot type specified */
    hasPlot( optionalPlotType) {
        if( optionalPlotType ) {
            return this.state?.plot===optionalPlotType;
        }
        return this.state?.plot==="orderBy" || this.state?.plot==="groupBy";
    }

    groupBy( attr ) {
        this.big.hideBigCardsAndOutline();
        this.setState({ plot:"groupBy", attribute:attr });
        this.play.groupBy( this.dataSet, attr, this.home.smallCards  );
    }

    orderBy( attr ) {
        this.big.hideBigCardsAndOutline();
        this.setState({ plot:"orderBy", attribute:attr });
        this.play.orderBy( this.dataSet, attr, this.home.smallCards );
    }

    onStackClick() {
        this.options.isSpreadOut = !this.options.isSpreadOut;
        this.play.updateVisuals( false );
    }

    onIconsClick() {
        this.options.isIconOnly = !this.options.isIconOnly;
        this.home.setCardsIconOnly( this.options.isIconOnly );
        this.play.updateVisuals( false );
    }

    onCountClick() {
        this.options.isShowingCount = !this.options.isShowingCount;
        this.play.updateVisuals( false );
    }

    resetPlay() {
        this.play.resetPlay();
        this.big.resetBigCards( false ); // don't animate home yet
        this.options.isIconOnly = false; // undo isIconOnly
        this.home.setCardsIconOnly( this.options.isIconOnly );
        this.home.animateAllCardsHome();
        this.layerElements(); // undo layering changes from order-by
        this.Dyn.toolTip.hide(); // fix bug where tooltip persists on firefox after drop
        this.Dyn.assert( this.state.plot==="home" );
    }

    layerElements() {
        let playLayersCards = this.hasPlot();
        this.home.layerElements( !playLayersCards );
        this.big.layerElements();
        this.play.layerElements( playLayersCards );
    }

    /** remove a small card (e.g. if filter changed), make sure it is removed everywhere */
    removeCard( smallCard ) {
        //this.home.removeCard( smallCardsIndex );
        this.big.removeCard( smallCard );
        this.play.removeCard( smallCard );
        smallCard.destroy();
    }

    /** Handle a request to switch out of Play view and into Elementary Plot View
     * returns true if it is OK to switch, plus a plot state similar to the play view
     */
    switchOutOfPlayView() {
        // note: we must get a valid state even if not in OrderBy or GroupBy state, for example if there is a filter that removes all cards; see canDisablePlotViewButton()
        let state = {
            canSwitch: true, // Note: no longer used, consider removing in future versions --CM 6/24/2024.
            playState: this.save(),
            plotState: {
                // PLOT STATE: to match play state
                // for now, we assume that we only need to specify options that are different from the initial, mixed-up plot
                "xAxisView": {},
                "yAxisView": {
                    "titleString": ""
                },
                "topView": {},
                "legendView": {},
                "plotShaper": {
                    "iconStyler": this.home.iconStyler.save(),
                    "plotElements": [],
                },
                "dataSet": {}
            },
            "version": 2
        };
        // add plot elements
        let plotElements = state.plotState.plotShaper.plotElements;
        if( this.hasPlot()) {
            plotElements.push( { "primaryType": "controlsDotPlot" } );
            if( this.options.isShowingCount ) {
                plotElements.push( {"primaryType":"controlsCount","controlsCellCount":true} );
            }
        } else {
            plotElements.push( { "primaryType": "controlsMixup" } );
        }
        // add OrderBy/GroupBy attribute as the X-axis and Legend attribute
        let dataSet = state.plotState.dataSet,
            xAxisView = state.plotState.xAxisView;
        if( this.state.attribute?.id ) {
            xAxisView.attributeID = [ this.state.attribute.id ];
            dataSet.selectedAttributeId =
                dataSet.orderByAttributeId = this.state.attribute.id;
        }
        // add cases in big cards as the selected/Case Card case
        let bigCardCases = this.state.cases?.flat() || [];
        if( bigCardCases.length > 0 ) {
            dataSet.selectedCases = bigCardCases;
        }
        return( state );
    }

    /** switch from a plot view to a play view, converting the plot types where possible */
    switchIntoPlayView( plotState ) {
        let playState = {};
        if( plotState?.plotShaper ) {
            // convert the plot view
            let attributeIDs = plotState?.xAxisView?.attributeID,
                plotElements = plotState?.plotShaper?.plotElements,
                attribute = this.dataSet.getAttributeByID( attributeIDs ? attributeIDs[0] : "" ),
                dotPlot = plotElements.find((el) => el.primaryType==="controlsDotPlot"),
                count = plotElements.find((el) => el.primaryType==="controlsCount");
            if( attribute && dotPlot ) {
                playState = {
                    plot: attribute.stats.isNumeric() ? "orderBy" : "groupBy",
                    attributeID: attribute.id,
                    options: { isShowingCount: Boolean(count) }
                };
            } else {
                let selectedCases = plotState?.dataSet?.selectedCases || [];
                if( selectedCases.length > 0 ) {
                    playState = { plot:"bigCards", bigCards:{ cases:[] }};
                    if( Number.isFinite(selectedCases[0]))
                        playState.bigCards.cases.push( [selectedCases[0]] );
                    if( Number.isFinite(selectedCases[1]))
                        playState.bigCards.cases.push( [selectedCases[1]] );
                } else {
                    playState = { plot: "home" };
                }
            }
        }
        return playState;
    }

    /** respond to the start of the animation after dropping a small card */
    dropAnimationStart( dragResult ) {
        if( dragResult.result==="big card") { // if small card dropped onto a big card
            this.big.dropAnimationStart( dragResult );
        }
    }

    /** respond to the end of the animation after dropping a small card */
    dropAnimationEnd( dragResult ) {
        if( dragResult.result==="big card") { // if small card dropped onto a big card
            this.big.dropAnimationEnd( dragResult );
            //this.setState( this.big.getBigCardState()); issue 2197, set state on dropAnimationStart
        }
        this.updateHomeState();
        this.layerElements(); // undo layering changes from drag
    }

    /** check to see if cards are or are not home; update state settings to match */
    updateHomeState() {
        switch( this.state.plot ) {
            case "home":
            case "leftHome":
                this.setState( this.home.getHomeState());
            // else do nothing
        }
    }

    fastAnimation() {
        return {
            time: 700, // slower than Dyn.plotUtil.quickAnimationTimeMs
            easing: this.Dyn.plotUtil.quickAnimationEasing
        }
    }

    slowAnimation() {
        return {
            time: this.Dyn.plotUtil.stdAnimationTimeMs,
            easing: this.Dyn.plotUtil.stdAnimationEasing
        }
    }

    noAnimation() {
        return {
            time: 0,
            easing: this.Dyn.plotUtil.stdAnimationEasing
        }
    }

    iconColor() {
        return this.Dyn.plotUtil.kCaseColor;
    }

    getCardToolTip( caseIndex, smallCardIndex ) {
        let Dyn = this.Dyn,
            tip = "",
            tipAttributes = [],
            caseAttribute = this.dataSet.getAttributeByID('id');
        [ ...this.defaultTipAttributes, this.state.attribute, caseAttribute ].forEach((attr) => {
            if( attr && ! tipAttributes.includes( attr ))
                tipAttributes.push( attr );
        });
        tipAttributes.forEach((attr) => {
            if( tip ) {
                tip += "\n";
            }
            tip += Dyn.loc("{{attribute}}: {{value}}", {
                attribute: (attr.isCaseAttribute ? Dyn.loc("Card") : attr.name ),
                value: (attr.isCaseAttribute ? (smallCardIndex+1).toString() : attr.caseValueToString( caseIndex ))
            });
        });
        return tip;
    }
}
